import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import type { ActivityWithActor, ActivityType } from '@kinapp/shared';

interface ActivityFeedProps {
  familyId: string;
  dependentIds?: string[];
  limit?: number;
  onRefresh?: () => void;
}

const activityMessages: Record<ActivityType, (activity: ActivityWithActor) => string> = {
  TASK_CREATED: (a) => `created task "${a.metadata?.title}"`,
  TASK_COMPLETED: (a) => `completed task "${a.metadata?.title}"`,
  TASK_REASSIGNED: (a) => {
    const from = a.metadata?.oldAssignee || 'unassigned';
    const to = a.metadata?.newAssignee || 'unassigned';
    return `reassigned "${a.metadata?.title}" from ${from} to ${to}`;
  },
  MEDICATION_LOGGED: (a) => `logged ${a.metadata?.name} as ${a.metadata?.status?.toLowerCase()}`,
  APPOINTMENT_CREATED: (a) => `scheduled appointment "${a.metadata?.title}"`,
  MEMBER_JOINED: (a) => `joined the family as ${a.metadata?.role?.toLowerCase()}`,
  INVITE_SENT: (a) => `invited ${a.metadata?.email}`,
  DIARY_ENTRY: () => `added a diary entry`,
  STAFF_JOINED: () => `joined the care home`,
  STAFF_SCHEDULE_UPDATED: () => `updated the staff schedule`,
  FAMILY_LINKED: () => `linked a family`,
  FAMILY_UNLINKED: () => `unlinked a family`,
  CUSTODY_SCHEDULE_CREATED: () => `set up a custody schedule`,
  CUSTODY_OVERRIDE_ADDED: (a) => `added a custody override${a.metadata?.reason ? ` (${a.metadata.reason})` : ''}`,
  HANDOVER_CONFIRMED: () => `confirmed a custody handover`,
  HANDOVER_MISSED: () => `marked a handover as missed`,
  SCHEDULED_ACTIVITY_CREATED: (a) => `added activity "${a.metadata?.name}"`,
  SCHEDULED_ACTIVITY_UPDATED: (a) => `updated activity "${a.metadata?.name}"`,
  SCHEDULED_ACTIVITY_CANCELLED: (a) => `removed activity "${a.metadata?.name}"`,
};

export function ActivityFeed({
  familyId,
  dependentIds,
  limit = 10,
}: ActivityFeedProps) {
  const [activities, setActivities] = useState<ActivityWithActor[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const dependentId = dependentIds?.length === 1 ? dependentIds[0] : undefined;

  useEffect(() => {
    loadActivities();
  }, [familyId, dependentIds?.join(',')]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const response = await api.getActivityFeed(familyId, dependentId, undefined, limit);
      setActivities(response.activities);
      setHasMore(response.hasMore);
      setCursor(response.nextCursor);
    } catch (err) {
      console.error('Failed to load activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await api.getActivityFeed(familyId, dependentId, cursor, limit);
      setActivities([...activities, ...response.activities]);
      setHasMore(response.hasMore);
      setCursor(response.nextCursor);
    } catch (err) {
      console.error('Failed to load more activities:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatTime = (date: string | Date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getMessage = (activity: ActivityWithActor): string => {
    const formatter = activityMessages[activity.type as ActivityType];
    return formatter ? formatter(activity) : 'performed an action';
  };

  if (loading) {
    return <div className="text-gray-500 text-sm py-4">Loading activity...</div>;
  }

  if (activities.length === 0) {
    return <div className="text-gray-500 text-sm py-4">No recent activity</div>;
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <div key={activity.id} className="flex items-start gap-3 text-sm">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
            {activity.actor.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900">
              <span className="font-medium">{activity.actor.name}</span>{' '}
              {getMessage(activity)}
              {activity.dependent && (
                <span className="text-gray-500">
                  {' '}for{' '}
                  {activity.type === 'DIARY_ENTRY' && activity.metadata?.dependentNames?.length > 0
                    ? (activity.metadata?.dependentNames as string[]).join(', ')
                    : activity.dependent.name}
                </span>
              )}
            </p>
            {activity.type === 'DIARY_ENTRY' && activity.metadata?.content && (
              <p className="mt-1 text-gray-600 bg-gray-50 p-2 rounded border border-gray-200">
                {activity.metadata.content}
              </p>
            )}
            <p className="text-gray-500 text-xs mt-0.5">{formatTime(activity.createdAt)}</p>
          </div>
        </div>
      ))}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          {loadingMore ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  );
}
